import {
  createFileRoute,
  Outlet,
  redirect,
  useChildMatches,
  useNavigate,
} from '@tanstack/react-router'
import { useMemo } from 'react'
import {
  conditionSearchParam,
  decodeConditions,
  encodeConditions,
} from '@/components/condition-filter'
import { journalConditionFields } from '@/components/condition-filter/accounting-fields'
import {
  JOURNAL_ENTRY_STATUSES,
  JOURNAL_ENTRY_TYPES,
  type JournalEntryFilters,
  type JournalSortColumn,
} from '@/features/accounting/api'
import { AccountingJournalRegisterScreen } from '@/features/accounting/journal-register-screen'
import { hasGrant } from '@/lib/me'

const SORT_COLUMNS: JournalSortColumn[] = ['date', 'amount']
const SORT_DIRECTIONS = ['asc', 'desc']

function isSortColumn(value: unknown): value is JournalSortColumn {
  return typeof value === 'string' && (SORT_COLUMNS as string[]).includes(value)
}

function isSortDirection(value: unknown): value is 'asc' | 'desc' {
  return typeof value === 'string' && SORT_DIRECTIONS.includes(value)
}

function isOneOf<T extends string>(values: readonly T[], value: unknown): value is T {
  return typeof value === 'string' && (values as readonly string[]).includes(value)
}

/** A URL carries the word, not the boolean. */
function toBoolean(value: unknown): boolean | undefined {
  if (value === true || value === 'true') return true
  if (value === false || value === 'false') return false
  return undefined
}

function toPage(value: unknown): number | undefined {
  if (typeof value !== 'string' && typeof value !== 'number') return undefined
  const page = Number.parseInt(String(value), 10)
  return Number.isInteger(page) && page > 0 ? page : undefined
}

export const Route = createFileRoute('/_authenticated/accounting/journal')({
  validateSearch: (search: Record<string, unknown>): JournalEntryFilters => ({
    c: conditionSearchParam(search.c),
    match: search.match === 'any' ? 'any' : undefined,
    q: typeof search.q === 'string' ? search.q : undefined,
    source: typeof search.source === 'string' ? search.source : undefined,
    journal_ids: Array.isArray(search.journal_ids)
      ? search.journal_ids.filter((id): id is string => typeof id === 'string')
      : typeof search.journal_ids === 'string'
        ? [search.journal_ids]
        : undefined,
    from: typeof search.from === 'string' ? search.from : undefined,
    to: typeof search.to === 'string' ? search.to : undefined,
    status: isOneOf(JOURNAL_ENTRY_STATUSES, search.status) ? search.status : undefined,
    account: typeof search.account === 'string' ? search.account : undefined,
    entryType: isOneOf(JOURNAL_ENTRY_TYPES, search.entryType) ? search.entryType : undefined,
    costCenter: typeof search.costCenter === 'string' ? search.costCenter : undefined,
    adjusting: toBoolean(search.adjusting),
    minAmount: typeof search.minAmount === 'string' ? search.minAmount : undefined,
    maxAmount: typeof search.maxAmount === 'string' ? search.maxAmount : undefined,
    sort: isSortColumn(search.sort) ? search.sort : undefined,
    dir: isSortDirection(search.dir) ? search.dir : undefined,
    page: toPage(search.page),
    perPage: toPage(search.perPage),
  }),
  beforeLoad: ({ context }) => {
    if (
      !hasGrant(context.queryClient, 'accounting_journal.create') &&
      !hasGrant(context.queryClient, 'accounting_report.view')
    ) {
      throw redirect({ to: '/accounting' })
    }
  },
  component: AccountingJournalRoute,
})

/**
 * `/accounting/journal` is the register; `/accounting/journal/new` is the
 * manual-entry form that used to occupy the bare path.
 *
 * A parent route that renders its list without ever rendering `<Outlet />`
 * silently swallows its children — the bug `hr/leave.tsx` documents and that
 * `hr/assets.tsx` and `accounting/assets.tsx` were both caught by. Hence the
 * `useChildMatches` check rather than an unconditional render.
 *
 * Reading the register needs only the report grant; posting needs
 * `accounting_journal.create`, which is what gates the New entry button and
 * the child route. `JournalEntryPolicy::viewAny` allows either, so this and
 * the API agree.
 *
 * The filters and the column sort live in the URL search params so a
 * register view is linkable and survives a reload (R-13).
 */
function AccountingJournalRoute() {
  const navigate = useNavigate()
  const children = useChildMatches()
  const search = Route.useSearch()
  const { conditions, match } = useMemo(
    () => decodeConditions({ c: search.c, match: search.match }, journalConditionFields),
    [search.c, search.match],
  )
  const params = { ...search, conditions, match }

  if (children.length > 0) {
    return <Outlet />
  }

  return (
    <AccountingJournalRegisterScreen
      params={params}
      onParamsChange={(next) =>
        void navigate({
          to: '/accounting/journal',
          search: {
            ...next,
            ...encodeConditions(next.conditions ?? [], next.match ?? 'all'),
            conditions: undefined,
          },
        })
      }
    />
  )
}
